import { useCallback, useEffect, useState } from "react";
import { fetchFoodAraRefreshStatus } from "../../../../api/foodApi";
import {
  FOOD_SOURCE_KEYS,
  foodRetailSourceLabel,
  isFoodRetailPlaybookSource,
} from "../../../../constants/foodSourceConstants";
import AdminTableContainer from "../layout/AdminTableContainer";

const STATUS_LABELS = {
  complete: "Complete",
  attention: "Needs attention",
  pending: "Pending",
  ready: "Ready",
  info: "Manual step",
};

function statusClass(status) {
  if (status === "complete" || status === "ready") return "food-status-badge--success";
  if (status === "attention" || status === "pending") return "food-status-badge--blocked";
  return "food-status-badge--dry-run-success";
}

const REFRESH_STATUS_CLI = {
  [FOOD_SOURCE_KEYS.ARA_COLOMBIA]: "npm run food:ara:refresh-status",
  [FOOD_SOURCE_KEYS.EXITO_SUPERMARKET]: "npm run food:exito:refresh-status",
  [FOOD_SOURCE_KEYS.CARULLA_SUPERMARKET]: "npm run food:carulla:refresh-status",
  [FOOD_SOURCE_KEYS.JUMBO_COLOMBIA]: "npm run food:jumbo:refresh-status",
  [FOOD_SOURCE_KEYS.D1_COLOMBIA]: "npm run food:d1:refresh-status",
  [FOOD_SOURCE_KEYS.OLIMPICA_COLOMBIA]: "npm run food:olimpica:refresh-status",
  [FOOD_SOURCE_KEYS.SUPERMU_COLOMBIA]: "npm run food:supermu:refresh-status",
};

const FoodRetailManualRefreshPlaybookSection = ({ sourceKey }) => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const sourceLabel = foodRetailSourceLabel(sourceKey);

  const loadReport = useCallback(async () => {
    if (!sourceKey || !isFoodRetailPlaybookSource(sourceKey)) {
      setReport(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setErrorMessage("");
      const payload = await fetchFoodAraRefreshStatus({ sourceKey });
      setReport(payload);
    } catch (error) {
      setErrorMessage(error.message || "Failed to load refresh status.");
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [sourceKey]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  if (!sourceKey || !isFoodRetailPlaybookSource(sourceKey)) {
    return null;
  }

  const basket = report?.latestBasketResult;
  const obs = report?.observationSummary;
  const comparison = report?.comparisonReadiness;
  const comparisonLabel = comparison?.baselineSourceLabel
    ? `${comparison.baselineSourceLabel} vs ${comparison.targetSourceLabel || sourceLabel}`
    : null;

  return (
    <section className="panel-card food-retail-ingestion-page__normalization">
      <div className="food-retail-ingestion-page__section-header">
        <div>
          <h2>{sourceLabel} Manual Refresh Playbook</h2>
          <p className="muted">
            Read-only status for the weekly PoC manual refresh workflow. Follow{" "}
            <code>{report?.playbookPath || "docs/food/"}</code> — no one-click run-all automation.
          </p>
        </div>
        <button type="button" className="btn btn-secondary" onClick={loadReport} disabled={loading}>
          {loading ? "Refreshing…" : "Refresh status"}
        </button>
      </div>

      {errorMessage ? <p className="form-error">{errorMessage}</p> : null}
      {loading && !report ? <p className="muted">Loading refresh status…</p> : null}

      {report ? (
        <>
          <div className="food-retail-ingestion-page__kpi-row">
            <div>
              <span className="muted">Observations</span>
              <strong>{obs?.total ?? "—"}</strong>
            </div>
            <div>
              <span className="muted">Fresh (72h)</span>
              <strong>{obs?.fresh ?? "—"}</strong>
            </div>
            <div>
              <span className="muted">Basket coverage</span>
              <strong>
                {basket?.pricedItemCount ?? "—"} / {basket?.totalItemsCount ?? 14}
              </strong>
            </div>
            <div>
              <span className="muted">Status</span>
              <strong>{basket?.status ?? "—"}</strong>
            </div>
            {comparisonLabel ? (
              <div>
                <span className="muted">{comparisonLabel}</span>
                <strong>{comparison?.ready ? "Ready" : "Incomplete"}</strong>
              </div>
            ) : null}
            <div>
              <span className="muted">Snapshot doc</span>
              <strong>
                {report.snapshotDocUpdatedAt
                  ? new Date(report.snapshotDocUpdatedAt).toLocaleDateString("es-CO")
                  : "—"}
              </strong>
            </div>
          </div>

          {report.nextRecommendedAction ? (
            <p className="muted food-retail-ingestion-page__hint">{report.nextRecommendedAction}</p>
          ) : null}

          <div className="food-retail-ingestion-page__hint">
            <p className="muted">
              CLI: <code>{REFRESH_STATUS_CLI[sourceKey] || "npm run food:ara:refresh-status"}</code>
            </p>
          </div>

          <AdminTableContainer>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Step</th>
                  <th>Title</th>
                  <th>Status</th>
                  <th>Type</th>
                  <th>Detail</th>
                  <th>Link</th>
                </tr>
              </thead>
              <tbody>
                {(report.playbookSteps || []).map((step) => (
                  <tr key={step.id}>
                    <td>{step.number}</td>
                    <td>{step.title}</td>
                    <td>
                      <span className={`food-status-badge ${statusClass(step.status)}`}>
                        {STATUS_LABELS[step.status] || step.status}
                      </span>
                    </td>
                    <td>{step.mutates ? "Mutates" : "Read-only"}</td>
                    <td className="muted">{step.detail || "—"}</td>
                    <td>
                      {step.adminLink ? (
                        <a className="btn btn--ghost btn--sm" href={step.adminLink}>
                          Open
                        </a>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </AdminTableContainer>
        </>
      ) : null}
    </section>
  );
};

export default FoodRetailManualRefreshPlaybookSection;
